// Panel para escanear/reparar duplicados históricos de tesorería.
// Consume /api/admin/treasury/duplicates y /api/admin/treasury/duplicates/repair.
// Motivo (incidente COB-000035): antes del fix idempotente del 16/09 quedaron
// documentos con el mismo número visible pero distintos IDs internos.

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { AlertTriangle, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABLE_LABELS = {
  collections: 'Cobranzas',
  payment_orders: 'Órdenes de Pago',
  adjustments: 'Ajustes',
  transfers: 'Transferencias',
};

const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString('es-AR'); } catch { return iso; } };

export function DuplicatesRepairPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // { table, number, docs }
  const [lastRepairResult, setLastRepairResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/treasury/duplicates`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al escanear duplicados');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doRepair = async () => {
    if (!confirmTarget) return;
    setRepairing(true);
    try {
      const params = new URLSearchParams({ table: confirmTarget.table, number: confirmTarget.number });
      const r = await axios.post(`${BACKEND_URL}/api/admin/treasury/duplicates/repair?${params}`, {}, { withCredentials: true });
      setLastRepairResult(r.data);
      toast.success(`Saneamiento OK: ${r.data.total_documents_removed} documento(s) eliminado(s)`);
      setConfirmTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reparar');
    } finally { setRepairing(false); }
  };

  if (loading) return <Card><CardContent className="pt-6 text-center text-muted-foreground">Escaneando…</CardContent></Card>;
  if (!data) return null;

  const total = data.summary.total_extra_documents;

  return (
    <>
      <Card data-testid="dup-repair-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              {total === 0 ? <ShieldCheck className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
              Duplicados históricos de Tesorería
            </CardTitle>
            <Button size="sm" variant="outline" onClick={load} data-testid="dup-repair-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refrescar
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Escanea las 4 tablas de tesorería y detecta números de comprobante repetidos por race conditions previas al fix idempotente del 16/09.
            El saneamiento mantiene el registro más antiguo, elimina los duplicados junto con sus dependientes (asientos contables, movimientos de tesorería, cta.cte del residente/proveedor) y <b>ajusta automáticamente el `current_balance` del banco</b>. Los reportes (Libro Diario, IVA, Cta.Cte, tesorería) se recalculan al vuelo.
            <span className="block mt-1 text-amber-700">
              ⚠ Acción destructiva. Se genera backup JSON en <code>/app/backups/dup_repair_*/</code> antes de tocar nada. Requiere <b>rol admin</b>.
            </span>
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {total === 0 ? (
            <div className="text-center py-6 text-emerald-700 bg-emerald-50 rounded border border-emerald-200" data-testid="dup-repair-clean">
              ✅ No hay duplicados históricos. Todo limpio.
            </div>
          ) : (
            Object.entries(data.duplicates).map(([table, groups]) => (
              groups.length > 0 && (
                <div key={table} data-testid={`dup-table-${table}`}>
                  <h4 className="font-semibold text-sm mb-2">
                    {TABLE_LABELS[table]}
                    <Badge className="ml-2 bg-amber-500">{groups.length} grupo(s)</Badge>
                  </h4>
                  <div className="space-y-2">
                    {groups.map((g) => (
                      <div key={g.number} className="border rounded p-3 bg-amber-50/40" data-testid={`dup-group-${g.number}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-mono font-medium">{g.number}</span>
                            <Badge className="ml-2 bg-red-500">{g.duplicate_count} veces</Badge>
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setConfirmTarget({ table, number: g.number, docs: g.documents })}
                            data-testid={`repair-btn-${g.number}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Sanear
                          </Button>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-0.5">
                          {g.documents.map((d, i) => (
                            <div key={i} className={i === 0 ? 'text-emerald-700 font-medium' : ''}>
                              {i === 0 && '✓ (se mantiene) '}
                              {i > 0 && '✗ (se elimina) '}
                              {fmtDT(d.created_at)} · {fmtMoney(d.total_amount || d.amount)} · {d.description || '(sin descripción)'}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))
          )}
          {lastRepairResult && (
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-sm mb-2">Último saneamiento</h4>
              <div className="text-xs bg-slate-50 p-3 rounded font-mono">
                <div>Backup: {lastRepairResult.backup_dir}</div>
                <div>Grupos: {lastRepairResult.groups_repaired} · Docs eliminados: {lastRepairResult.total_documents_removed}</div>
                {(lastRepairResult.affected_cash_banks_diagnostic || []).map((b, i) => (
                  <div key={i} className="mt-1">
                    Banco <b>{b.name}</b>: saldo actual {fmtMoney(b.current_balance)}, movs neto {fmtMoney(b.movement_net)}, diferencia {fmtMoney(b.gap_vs_current_balance)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!confirmTarget} onOpenChange={(v) => !v && setConfirmTarget(null)}>
        <DialogContent
          data-testid="dup-repair-confirm"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Confirmar saneamiento
            </DialogTitle>
            <DialogDescription>Esta acción es destructiva e irreversible (aunque queda backup automático).</DialogDescription>
          </DialogHeader>
          {confirmTarget && (
            <div className="text-sm space-y-2">
              <p>Vas a sanear <b>{confirmTarget.number}</b> ({TABLE_LABELS[confirmTarget.table]}):</p>
              <ul className="list-disc pl-6 text-xs">
                <li>Se mantendrá <b>1 registro</b> (el más antiguo por `created_at`).</li>
                <li>Se eliminarán <b>{(confirmTarget.docs?.length || 1) - 1} duplicado(s)</b>.</li>
                <li>En cascada: `resident_cc`, `supplier_cc`, `treasury_movements`, `journal_entries` referentes.</li>
                <li>`current_balance` del banco se ajusta automáticamente (revierte el neto duplicado).</li>
                <li>Backup JSON: <code>/app/backups/dup_repair_{confirmTarget.table}_&lt;ts&gt;/</code></li>
                <li>Audit log: acción "SANEÓ" con todo el detalle.</li>
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmTarget(null)} disabled={repairing}>Cancelar</Button>
            <Button variant="destructive" onClick={doRepair} disabled={repairing} data-testid="dup-repair-confirm-btn">
              {repairing ? 'Saneando…' : 'Confirmar y sanear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default DuplicatesRepairPanel;
